import React, { useState, useEffect, useRef } from 'react';

const pad = (n) => String(n).padStart(2, '0');

const formatTime = (ms) => {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  const centis = Math.floor((ms % 1000) / 10);
  if (min > 0) return `${pad(min)}:${pad(sec)}`;
  return `${pad(sec)}.${pad(centis)}s`;
};

const formatClock = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const MoveRequestIndicator = ({ moveRequestStatus }) => {
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (moveRequestStatus?.state === 'pending') {
      setElapsed(0);
      intervalRef.current = setInterval(() => {
        setElapsed(Date.now() - (moveRequestStatus.startTime || Date.now()));
      }, 50);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [moveRequestStatus?.state, moveRequestStatus?.startTime]);

  if (!moveRequestStatus || moveRequestStatus.state === 'idle') return null;

  const { state, durationMs, endTime } = moveRequestStatus;

  if (state === 'pending') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          background: 'rgba(0,0,0,0.25)',
          borderRadius: 3,
          padding: '0 7px',
          height: 16,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.03em',
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            border: '1.5px solid rgba(255,255,255,0.4)',
            borderTopColor: '#fff',
            display: 'inline-block',
            animation: 'mrSpin 0.7s linear infinite',
          }}
        />
        <span style={{ color: '#fde68a' }}>Move Request</span>
        <span style={{ color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{formatTime(elapsed)}</span>
        <style>{`@keyframes mrSpin { to { transform: rotate(360deg); } }`}</style>
      </span>
    );
  }

  if (state === 'success') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          background: 'rgba(0,0,0,0.25)',
          borderRadius: 3,
          padding: '0 7px',
          height: 16,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.03em',
        }}
      >
        <span style={{ color: '#4ade80' }}>✓</span>
        <span style={{ color: '#fde68a' }}>Move Request</span>
        <span style={{ color: 'rgba(255,255,255,0.7)', fontVariantNumeric: 'tabular-nums' }}>
          {formatClock(endTime)}
        </span>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 9 }}>
          ({formatTime(durationMs)})
        </span>
      </span>
    );
  }

  if (state === 'error') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          background: 'rgba(0,0,0,0.25)',
          borderRadius: 3,
          padding: '0 7px',
          height: 16,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.03em',
        }}
      >
        <span style={{ color: '#f87171' }}>✗</span>
        <span style={{ color: '#fde68a' }}>Move Request</span>
        <span style={{ color: 'rgba(255,255,255,0.7)', fontVariantNumeric: 'tabular-nums' }}>
          {formatClock(endTime)}
        </span>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 9 }}>
          ({formatTime(durationMs)})
        </span>
      </span>
    );
  }

  return null;
};

const FlowChartStatusBar = ({ saveStatus, isDesignMode, showCodeEditor, selectedShape, moveRequestStatus }) => {
  return (
    <div
      style={{
        background: '#007acc',
        height: 22,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        fontSize: 11,
        color: 'white',
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      {/* Sol: Ready + Save status + Move Request */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span>🟢 Ready</span>

        {saveStatus && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'rgba(0,0,0,0.2)',
              borderRadius: 3,
              padding: '0 6px',
              height: 16,
              fontSize: 10,
            }}
          >
            <span style={{ color: saveStatus.type === 'success' ? '#4ade80' : '#f87171' }}>
              {saveStatus.type === 'success' ? '✓' : '✗'}
            </span>
            {saveStatus.message}
          </span>
        )}

        <MoveRequestIndicator moveRequestStatus={moveRequestStatus} />
      </div>

      {/* Sağ: Design Mode + Code Editor */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <span>Design Mode: {isDesignMode ? 'ON' : 'OFF'}</span>
        <span>Code Editor: {showCodeEditor && selectedShape ? 'OPEN' : 'CLOSED'}</span>
      </div>
    </div>
  );
};

export default FlowChartStatusBar;
